import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, futureValueCompound, compoundGrowthByYear } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function CompoundInterestPage() {
    const [principal, setPrincipal] = useState(10000);
    const [monthly, setMonthly] = useState(500);
    const [rate, setRate] = useState(8);
    const [years, setYears] = useState(25);

    const { fv, series, totalContrib, totalInterest } = useMemo(() => {
        const fv = futureValueCompound(toNum(principal), toNum(monthly), toNum(rate), toNum(years));
        const series = compoundGrowthByYear(toNum(principal), toNum(monthly), toNum(rate), toNum(years));
        const totalContrib = toNum(principal) + toNum(monthly) * toNum(years) * 12;
        return { fv, series, totalContrib, totalInterest: fv - totalContrib };
    }, [principal, monthly, rate, years]);

    return (
        <CalculatorShell
            slug="compound-interest"
            explainer={
                                <>
                    <h2>How this compound interest calculator works</h2>
                    <p>
                        A compound interest calculator shows how an investment or savings balance grows when
                        returns earn returns. It's the foundational tool for understanding the long-term impact
                        of saving early, investing consistently, and letting time do the heavy lifting.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Compound formula:</strong> A = P(1 + r/n)^(nt), where n is compounding frequency and t is time in years.</li>
                        <li><strong>Compounding frequency:</strong> daily compounding produces more growth than monthly or annual — though the difference narrows at lower rates.</li>
                        <li><strong>Regular contributions:</strong> factored in separately and each contribution compounds from the date it's made.</li>
                        <li><strong>Rule of 72:</strong> divide 72 by your interest rate to estimate how many years it takes to double your money.</li>
                        <li><strong>Inflation adjustment:</strong> shows real purchasing power of the final balance, not just the nominal figure.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Starting early beats investing more later — a 10-year head start can matter more than doubling contributions.</li>
                        <li>Reinvest dividends automatically so every payout compounds rather than sitting idle.</li>
                        <li>Use the calculator to see the concrete dollar cost of waiting one, two, or five more years to start.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Starting amount" prefix="$" value={principal} onChange={setPrincipal} dataTestId="calc-input-principal" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Monthly contribution" prefix="$" value={monthly} onChange={setMonthly} dataTestId="calc-input-monthly" />
                        <Field label="Annual return" suffix="%" value={rate} onChange={setRate} step={0.25} dataTestId="calc-input-rate" />
                    </div>
                    <Field label="Years to grow" value={years} onChange={setYears} dataTestId="calc-input-years" />
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Final value"
                        value={fv}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "You put in", value: totalContrib, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-contrib" },
                            { label: "Growth", value: totalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-growth" },
                        ]}
                    />
                </div>
            </div>
            <div className="mt-10 bg-card border border-border rounded-xl p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Growth curve</div>
                <h3 className="font-serif text-2xl text-primary mt-1 mb-4">Contributions vs. growth</h3>
                <figure aria-label={`Projected compound growth of ${fmtUSD(toNum(principal))} starting balance plus ${fmtUSD(toNum(monthly))} monthly contributions at ${toNum(rate)}% annual return over ${toNum(years)} years, ending at ${fmtUSD(fv, { decimals: 0 })}`}>
                    <div className="h-72 w-full" data-testid="calc-chart" style={{ minHeight: 288 }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={288}>
                        <AreaChart data={series}>
                            <defs>
                                <linearGradient id="gContrib" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(146 28% 35%)" stopOpacity={0.5} />
                                    <stop offset="95%" stopColor="hsl(146 28% 35%)" stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="gInt" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(46 64% 52%)" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="hsl(46 64% 52%)" stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(42 22% 86%)" vertical={false} />
                            <XAxis dataKey="year" stroke="hsl(150 12% 45%)" tick={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} />
                            <YAxis stroke="hsl(150 12% 45%)" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                            <Tooltip
                                contentStyle={{ background: "white", border: "1px solid #E5E0D5", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                                formatter={(v, k) => [fmtUSD(v, { decimals: 0 }), k === "contributions" ? "Contributions" : k === "balance" ? "Total balance" : "Growth"]}
                            />
                            <Area type="monotone" dataKey="contributions" stackId="1" stroke="hsl(146 28% 35%)" fill="url(#gContrib)" strokeWidth={2} />
                            <Area type="monotone" dataKey="interest" stackId="1" stroke="hsl(46 64% 52%)" fill="url(#gInt)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                    <figcaption className="sr-only">
                        Final projected balance {fmtUSD(fv, { decimals: 0 })} composed of {fmtUSD(totalContrib, { decimals: 0 })} in contributions and {fmtUSD(totalInterest, { decimals: 0 })} in compound growth.
                    </figcaption>
                </figure>
            </div>
        </CalculatorShell>
    );
}
